import {
  Box,
  Button,
  Select,
  SelectProps,
  SpaceBetween,
  Grid,
  Header,
} from '@amzn/awsui-components-core';
import arraySort from 'array-sort';
import {
  ContentRequest,
  ContentRequestState,
  ContentType,
  UpdateContentRequestResponse,
} from '@amzn/awscat-pattern-library-service-model';
import React, { useEffect, useState } from 'react';

import { IMplServiceClient } from '../../api/IMplServiceClient';
import { MplServiceClientFactory } from '../../api/MplServiceClientFactory';
import { IFeedbackServiceClient, FeedbackServiceFactory } from '../../api/FeedbackService';
import {
  getContentRequestTagLabels,
  getDefaultFilterSelection,
  getUserRoles,
} from '../../api/UtilsApi';
import constants from '../../common/constants';
import RouterPaths from '../../common/RouterPaths';
import { withAppContext } from '../../contexts/AppContext';
import AppContextProps from '../../contexts/AppContextProps';
import BrowserHistory from '../utils/BrowserHistory';
import ContentRequestListView from './ContentRequestListView';
import ContentRequestCatalogView from './ContentRequestCatalogView';
import ContentRequestViewSelector, { ContentRequestView } from './ContentRequestViewSelector';
import ContentRequestFilters from './ContentRequestFilters';
import {
  FilterCategory,
  FilterOption,
  Filters,
  filterOptionToLabelMap,
} from '../content/ContentFilters';
import { getFiltersToApply } from '../utils/ContentFilterUtils';
import { getTemplateMetadataFromContentRequest } from '../../api/UtilsApi';
import { FeedbackRating, FeedbackRatingSystem } from '../feedback-service';
import { localLog } from '../../loggers/Logger';
import '../../resources/styles/scss/components/content-request/all-suggestions.scss';
import { convertEntities } from '@amzn/awscat-editor';

const createdDateOrderOption = getDefaultFilterSelection(
  'createdDate',
  constants.CONTENT_REQUEST_ORDER_BY_CREATED_DATE_LABEL,
);
const assignmentDateOrderOption = getDefaultFilterSelection(
  'assignedDate',
  constants.CONTENT_REQUEST_ORDER_BY_ASSIGNMENT_DATE_LABEL,
);
const totalVotesOrderOption = getDefaultFilterSelection(
  'totalVotes',
  constants.CONTENT_REQUEST_ORDER_BY_TOTAL_LIKES_LABEL,
);
const orderByOptions = [assignmentDateOrderOption, createdDateOrderOption, totalVotesOrderOption];

const filterOptionToMetadataMap = new Map<string, string>([
  [FilterCategory.ContentType, ''],
  [FilterCategory.State, ''],
  [FilterCategory.TechDomains, ''],
  [FilterCategory.AwsServices, ''],
  [FilterCategory.Workload, ''],
]);

const filterOptionToValuesMap = new Map<string, string[]>([
  [FilterCategory.ContentType, ['contentType']],
  [FilterCategory.State, ['state']],
  [FilterCategory.TechDomains, ['mainTechDomain', 'techDomains']],
  [FilterCategory.AwsServices, ['awsServices']],
  [FilterCategory.Workload, ['workload']],
]);

export interface ContentRequestWithMetaData extends ContentRequest {
  FilterCategory?: string | string[];
}

const cardEmptyState = (
  <Box textAlign="center" padding={{ top: 'xs' }} margin={{ bottom: 'm' }}>
    <b>{constants.CONTENT_REQUEST_NO_SUGGESTION}</b>
  </Box>
);

const cardNoMatchState = (
  <Box textAlign="center" padding={{ top: 'xs' }} margin={{ bottom: 'm' }}>
    <b>{constants.CONTENT_REQUEST_NO_MATCH}</b>
  </Box>
);

const mplServiceClient: IMplServiceClient = MplServiceClientFactory.create();
const feedbackServiceClient: IFeedbackServiceClient = FeedbackServiceFactory.create();

const updateContentRequestWithMetaData = (
  contentRequests: ContentRequest[],
): ContentRequestWithMetaData[] => {
  const contentRequestWithMetaData: ContentRequestWithMetaData[] = contentRequests.map(
    (contentRequest) => ({
      ...contentRequest,
      ...getTemplateMetadataFromContentRequest(contentRequest.contentRequestData.tags),
    }),
  );
  return contentRequestWithMetaData;
};

const updateTagLabel = (contentRequest: ContentRequest): ContentRequest => {
  const tagLabels = getContentRequestTagLabels(contentRequest.contentRequestData.tags);
  return {
    ...contentRequest,
    contentRequestData: {
      ...contentRequest.contentRequestData,
      tags: tagLabels,
    },
  };
};

const updateTagLabels = (contentRequests: ContentRequest[]): ContentRequest[] => {
  return contentRequests.map(updateTagLabel);
};

const AllContentSuggestionsView = (props: AppContextProps) => {
  const [isLoading, setLoadingState] = useState<boolean>(false);
  const [contentRequests, setContentRequests] = useState<ContentRequestWithMetaData[]>([]);
  const [allContentRequests, setAllContentRequests] = useState<ContentRequestWithMetaData[]>([]);
  const [orderByType, setOrderByType] = useState<SelectProps.Option>(totalVotesOrderOption);
  const [contentRequestsView, setContentRequestsView] = useState<ContentRequestView>(
    ContentRequestView.List,
  );

  const user = props.appContext.authnInfo!.userProfile;
  const { userId } = user;
  const userRoles = getUserRoles(props.appContext.authnInfo!.userProfile);
  const { entities } = props.appContext;

  useEffect(() => {
    const fetchSuggestions = async () => {
      setLoadingState(true);
      const listsOfContentRequest = await mplServiceClient.listContentRequests();
      const updatedContentRequests = updateTagLabels(listsOfContentRequest.contentRequests);
      const sortedContentRequest = sortContentRequests(updatedContentRequests);
      setContentRequests(updateContentRequestWithMetaData(sortedContentRequest));
      setAllContentRequests(updateContentRequestWithMetaData(sortedContentRequest));
      setLoadingState(false);
    };
    fetchSuggestions();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const sortContentRequests = (contentRequestToSort: ContentRequest[]) => {
    const sortKey = orderByType.value!;
    const sortedContentRequests = arraySort(
      [...contentRequestToSort],
      (cr1: ContentRequest, cr2: ContentRequest) => {
        const val1 = (cr1 as Record<string, any>)[sortKey];
        const val2 = (cr2 as Record<string, any>)[sortKey];
        if (!val1) {
          return 1;
        }
        if (!val2) {
          return -1;
        }
        if (orderByType.label === constants.CONTENT_REQUEST_ORDER_BY_TOTAL_LIKES_LABEL) {
          return val2 - val1;
        }
        return val1.toMillis() > val2.toMillis() ? -1 : 1;
      },
    );
    return sortedContentRequests;
  };

  useEffect(() => {
    const sortedContentRequests = sortContentRequests(contentRequests);
    setContentRequests(sortedContentRequests);
  }, [orderByType]); // eslint-disable-line react-hooks/exhaustive-deps

  const refreshContentRequest = (contentRequest: ContentRequest) => {
    const updatedContentRequest = updateTagLabel(contentRequest);
    const updatedContentRequests = contentRequests.reduce<ContentRequestWithMetaData[]>(
      (acc, cur) => {
        if (cur.requestId !== updatedContentRequest.requestId) {
          acc.push(cur);
        } else {
          acc.push({
            ...cur,
            ...updatedContentRequest,
          });
        }
        return acc;
      },
      [],
    );
    setContentRequests(updateContentRequestWithMetaData(updatedContentRequests));
    setAllContentRequests(updateContentRequestWithMetaData(updatedContentRequests));
  };

  const handleSuggestionUpdate = async (
    contentRequestId: string,
    state: ContentRequestState,
    assigneeId?: string,
  ): Promise<UpdateContentRequestResponse> => {
    const res = await mplServiceClient.updateContentRequests(contentRequestId, state, {
      assigneeId,
    });
    refreshContentRequest(res.contentRequest);
    return res;
  };

  const handleSuggestionDelete = async (requestId: string) => {
    await mplServiceClient.deleteContentRequest(requestId);
    const filterDeletedSuggestionFunc = (contentRequest: ContentRequestWithMetaData) =>
      contentRequest.requestId !== requestId;
    const updatedContentRequests = contentRequests.filter(filterDeletedSuggestionFunc);
    const updatedAllContentRequests = allContentRequests.filter(filterDeletedSuggestionFunc);
    setContentRequests(updatedContentRequests);
    setAllContentRequests(updatedAllContentRequests);
  };

  const handleAddSuggestion = () => {
    BrowserHistory.push(RouterPaths.CONTENT_REQUEST_CREATION);
  };

  const filterSuggestions = (item: ContentRequest, filterText: string) => {
    const filteringTextLowerCase = filterText.toLocaleLowerCase().trim();
    const contentRequest = item as ContentRequest;
    const { assigneeName, userName } = contentRequest;
    const { contentName, description } = contentRequest.contentRequestData;
    return (
      convertEntities(contentName, entities).toLocaleLowerCase().includes(filteringTextLowerCase) ||
      convertEntities(description, entities).toLocaleLowerCase().includes(filteringTextLowerCase) ||
      (assigneeName || '').toLocaleLowerCase().includes(filteringTextLowerCase) ||
      userName.toLocaleLowerCase().includes(filteringTextLowerCase)
    );
  };

  const updateOrderByType = ({ detail }: { detail: SelectProps.ChangeDetail }) =>
    setOrderByType(detail.selectedOption);

  const tableHeader = (
    <Header
      variant="h3"
      actions={
        <SpaceBetween direction="horizontal" size="xs">
          <Box color="text-body-secondary" padding={{ top: 'xxs' }}>
            Sort by
          </Box>
          <Select
            selectedOption={orderByType}
            onChange={updateOrderByType}
            options={orderByOptions}
            ariaRequired={false}
          />
          <div className="cr-divider-vertical" />
          <Button variant="primary" onClick={handleAddSuggestion}>
            {constants.CONTENT_REQUEST_ADD_SUGGESTION}
          </Button>
        </SpaceBetween>
      }
      counter={`(${contentRequests.length.toString()})`}
    >
      Suggestions
    </Header>
  );

  const applyFilter = (filterOptions: Filters) => {
    const filtersToApply = getFiltersToApply(filterOptions);
    if (filtersToApply.length) {
      const filteredContentRequests = allContentRequests.filter((contentRequest) => {
        return filtersToApply.every((filter) => {
          const hasRequireCodeFilter = filter.options.some(
            (option) => option.label === filterOptionToLabelMap.get(FilterCategory.RequireCode),
          );
          return filter.values.some((value) => {
            const filterValue = (contentRequest as Record<string, any>)[value];
            if (
              hasRequireCodeFilter &&
              contentRequest.contentRequestData.requireCode &&
              contentRequest.contentType === ContentType.Pattern
            ) {
              return true;
            }
            if (typeof filterValue === 'string') {
              return (
                filterValue &&
                filter.options.some((option: FilterOption) => {
                  if (
                    value === FilterCategory.ContentType &&
                    hasRequireCodeFilter &&
                    option.value === ContentType.Pattern
                  ) {
                    return false;
                  }
                  return option.value === filterValue;
                })
              );
            } else {
              return (
                filterValue &&
                filter.options.some((option: FilterOption) => filterValue.includes(option.value))
              );
            }
          });
        });
      });
      setContentRequests(filteredContentRequests);
    } else {
      setContentRequests(allContentRequests);
    }
  };

  const handleSuggestionVote = async (requestId: string, rating: string) => {
    const updatedContentRequests = contentRequests.reduce<ContentRequestWithMetaData[]>(
      (acc, cur) => {
        if (cur.requestId !== requestId) {
          acc.push(cur);
        } else {
          acc.push({
            ...cur,
            totalVotes:
              rating === FeedbackRating.Like
                ? (cur.totalVotes ?? 0) + 1
                : (cur.totalVotes ?? 0) - 1,
            hasRated: !cur.hasRated,
          });
        }
        return acc;
      },
      [],
    );
    setContentRequests(updateContentRequestWithMetaData(updatedContentRequests));
    setAllContentRequests(updateContentRequestWithMetaData(updatedContentRequests));
    try {
      if (rating === FeedbackRating.Like) {
        await feedbackServiceClient.submitFeedback(
          requestId,
          userId,
          rating,
          FeedbackRatingSystem.Like,
          '',
        );
      } else {
        await feedbackServiceClient.deleteFeedback(requestId, FeedbackRatingSystem.Like);
      }
    } catch (e) {
      // Revert optimistic vote update on both filtered and unfiltered lists
      setContentRequests(updateContentRequestWithMetaData(contentRequests));
      setAllContentRequests(updateContentRequestWithMetaData(contentRequests));
      localLog.error(e);
    }
  };

  const contentRequestsToView =
    contentRequestsView === ContentRequestView.List ? (
      <ContentRequestListView
        cardEmptyState={cardEmptyState}
        cardNoMatchState={cardNoMatchState}
        contentRequests={contentRequests}
        filterSuggestions={filterSuggestions}
        handleSuggestionUpdate={handleSuggestionUpdate}
        isTechWriter={userRoles.isTechWriter}
        isLoading={isLoading}
        tableHeader={tableHeader}
        user={user}
        isAdmin={userRoles.isAdmin}
        handleSuggestionDelete={handleSuggestionDelete}
        handleSuggestionVote={handleSuggestionVote}
      />
    ) : (
      <ContentRequestCatalogView contentRequests={contentRequests} />
    );

  return (
    <div className="content-request-list-view-container">
      <Grid gridDefinition={[{ colspan: 2 }, { colspan: 10 }]}>
        <ContentRequestFilters
          items={contentRequests}
          filterOptionToMetadataMap={filterOptionToMetadataMap}
          filterOptionToValuesMap={filterOptionToValuesMap}
          isLoading={isLoading}
          onFilterChanged={applyFilter}
        />
        <div>
          <ContentRequestViewSelector
            contentRequestView={contentRequestsView}
            setContentRequestView={setContentRequestsView}
          />
          {contentRequestsToView}
        </div>
      </Grid>
    </div>
  );
};

export default withAppContext(AllContentSuggestionsView);
